import React, { useState, useEffect, useRef } from 'react';
import { Modal, Typography, Button, Input, useToast, Alert as SpeculoAlert, AlertDescription, Checkbox } from '@crisellevaldez/speculo-ui';
import { RolloutLinkProvider, CredentialsManager } from '@rollout/link-react';
// Import CSS URL for dynamic loading (using Vite's ?url suffix)
import rolloutCssUrl from '@rollout/link-react/style.css?url';
import '../../../styles/rollout-overrides.css';
import { Organization } from '../../../types/organization.types';
import {
  MessageSquare,
  RotateCw,
  Edit,
  // Database,
  Code,
  // Radio,
  Settings,
  // LucideFileWarning,
  // Tag,
  Link,
  Check,
  X,
  Info,
} from 'lucide-react';
import { RolloutSyncStatusBanner, RolloutSyncResults } from './RolloutSyncStatusBanner';
import { formatPhoneNumber } from '../../../utils/formatters';
import { TwilioModal } from './TwilioModal';
import { CRMModal } from './CRMModal';
import WebsiteIntegrationModal from './WebsiteIntegrationModal';
import { CINCModal } from './CINCModal';
import { SpeculoOAuthModal } from './SpeculoOAuthModal';
import { getOAuthClientInfo } from '../../../api/speculoOAuth';
import { FubStagesModal } from './FubStagesModal';
import { FollowUpBossModal } from './FollowUpBossModal';
import { TipBox } from '../onboarding/components/TipBox';
import { useUser } from '../../../hooks/useUser';
import { useAuthStore } from '../../../contexts/auth/useAuthStore';
import { AdminLockOverlay } from './AdminLockOverlay';
import { useRolloutToken } from '../../../hooks/rollout/useRolloutToken';
import { useRolloutCrmConnection } from '../../../hooks/rollout/useRolloutCrmConnection';
import { validateSubdomain } from '../../../schemas/subdomain.schema';
import { isProductionTestCrmOrg, PRODUCTION_TEST_CRMS } from '../../../api/productionTestCrmReads';
import { ExistingProductionCrmBinding } from './ExistingProductionCrmBinding';
import {
  getProductionCrmConnectionStatus,
  isManagedCrmApp,
  type ProductionCrmConnectionStatus,
} from '../../../api/productionCrmConnections';
import {
  ALL_ROLLOUT_CRM_APP_KEYS,
  CRM_TRADEMARK_NOTICE,
  MANUAL_CONNECT_CRMS,
  MANUAL_CONNECT_CRM_IDS,
  ROLLOUT_CRMS,
  type ManualConnectCrmId,
  type RolloutCrmId,
} from '../../../constants/rolloutCrms';
import { getCrmConnectRequest } from '../../../utils/manualCrmConnect';
import { ManualCrmConnectModal } from './ManualCrmConnectModal';
import { useUpdateOrganizationSettings } from '../../../hooks/organizations/useUpdateOrganizationSettings';
import { useRolloutStages } from '../../../hooks/rollout/useRolloutStages';
import {
  hasAnyConnectedCrm,
  isCrmConnectedViaRollout,
} from '../../../utils/rollout';

// Legacy connector aliases only mark orgs that connected the old way as connected.
// New credentials must come from the current connector (e.g. BoldTrail's API-key
// `boldtrail`, never `boldtrail-oauth`).
const LEGACY_ROLLOUT_APP_KEYS: readonly string[] = ['cinc-pro', 'boldtrail-oauth'];
const currentRolloutAppKeys = (appKeys: readonly string[]): string[] =>
  appKeys.filter((appKey) => !LEGACY_ROLLOUT_APP_KEYS.includes(appKey));

const CONSENT_PROTECTED_CRM_APPS = ['cinc', 'sierra-interactive', 'boldtrail', 'lofty'];

interface IntegrationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedOrg?: Organization;
  /** Fresh ClientAdmin selection evidence; omitted by Header/onboarding. */
  selectedOrgId?: string;
  currentRowId?: string;
  freshDetailId?: string;
  freshDetailLoading?: boolean;
  onSuccess?: () => void;
  hideTwilio?: boolean;
  /** When true, only shows FUB integration (for onboarding). Legacy — prefer crmFilter. */
  fubOnly?: boolean;
  /** Restrict the Rollout connector list to a single CRM (onboarding per-CRM flow) */
  crmFilter?: RolloutCrmId;
  /** Require AI acknowledgment before showing Rollout connect controls */
  requireAIAcknowledgement?: boolean;
  /** Consent collected by the owning onboarding form when its gate is external. */
  onboardingConsentAcknowledged?: boolean;
  /** Optional custom copy for AI acknowledgment checkbox */
  aiAcknowledgementCopy?: string;
}

let nextRolloutAuthEpoch = 0;

const OrdinaryIntegrationsModal: React.FC<IntegrationsModalProps> = ({
  isOpen,
  onClose,
  selectedOrg,
  selectedOrgId,
  freshDetailId,
  freshDetailLoading,
  onSuccess,
  hideTwilio = false,
  fubOnly = false,
  crmFilter,
  requireAIAcknowledgement = true,
  onboardingConsentAcknowledged = false,
  aiAcknowledgementCopy = 'By checking the box and connecting your CRM and/or submitting data, you acknowledge and agree that you are solely responsible for ensuring that any contacts made available for outreach have provided appropriate consent to be contacted via automated systems, including AI-powered voice calls, dialing technology, text messaging, and email communications.\n\nYou further confirm that you have implemented appropriate tagging, filtering, and/or segmentation within your CRM to identify such contacts, and that only eligible contacts will be engaged by the system in compliance with all applicable laws, regulations, and communication consent requirements.',
}) => {
  // Derived filter: explicit crmFilter, else fubOnly → fub, else no restriction
  const effectiveCrmFilter: RolloutCrmId | undefined = crmFilter || (fubOnly ? 'fub' : undefined);
  const restricted = Boolean(effectiveCrmFilter);

  // Get user role to determine permissions
  const { user, loading: userLoading, error: userError, isAdmin, isSuperAdmin } =
    useUser({ revalidateRole: isOpen });
  const { user: authUser, loading: authLoading } = useAuthStore();
  const { showToast } = useToast();
  const authEpoch = useRef<{
    user: typeof authUser;
    userId: string | undefined;
    role: string | null;
    isAdmin: boolean;
    isSuperAdmin: boolean;
    authLoading: boolean;
    userLoading: boolean;
    count: number;
  } | null>(null);
  if (!authEpoch.current || authEpoch.current.user !== authUser ||
      authEpoch.current.userId !== user?.id || authEpoch.current.role !== (user?.role ?? null) ||
      authEpoch.current.isAdmin !== isAdmin || authEpoch.current.isSuperAdmin !== isSuperAdmin ||
      authEpoch.current.authLoading !== authLoading || authEpoch.current.userLoading !== userLoading) {
    authEpoch.current = { user: authUser, userId: user?.id, role: user?.role ?? null,
      isAdmin, isSuperAdmin, authLoading, userLoading, count: ++nextRolloutAuthEpoch };
  }
  // ClientAdmin's organization table is paginated; the selected org may be on another page.
  // Use its ID and fresh detail as readiness proof instead of requiring a current-page row.
  const selectionCurrent = selectedOrgId === undefined ||
    (selectedOrgId === selectedOrg?.id && freshDetailId === selectedOrgId &&
      freshDetailLoading === false);
  const widgetReady = isOpen && selectionCurrent && Boolean(selectedOrg?.id) &&
    Boolean(authUser?.uid) && !authLoading && !userLoading && !userError &&
    Boolean(user?.id) && isAdmin;
  const callbackIdentity = [isOpen, selectedOrg?.id, selectedOrgId,
    freshDetailId, freshDetailLoading, authUser?.uid, authLoading,
    user?.id, user?.role, userLoading, Boolean(userError), isAdmin, isSuperAdmin,
    authEpoch.current.count].join('|');
  const callbackContext = useRef({ identity: callbackIdentity, generation: 0 });
  if (callbackContext.current.identity !== callbackIdentity) {
    callbackContext.current = {
      identity: callbackIdentity, generation: callbackContext.current.generation + 1,
    };
  }
  const callbackGeneration = callbackContext.current.generation;
  const callbackMounted = useRef(false);
  useEffect(() => {
    callbackMounted.current = true;
    return () => { callbackMounted.current = false; };
  }, []);
  const isCurrentCallback = () => callbackMounted.current &&
    callbackContext.current.generation === callbackGeneration && widgetReady;
  // CINC, Lofty, BoldTrail and Sierra Interactive connect through Speculo's
  // Connect form. Orgs already connected to one through Rollout keep its tile.
  const crmSources = { crmConnections: selectedOrg?.crmConnections, rollout: selectedOrg?.rollout };
  const isManualCrmConnectedViaRollout = (crm: ManualConnectCrmId) =>
    ROLLOUT_CRMS[crm].appKeys.some((appKey) => isCrmConnectedViaRollout(crmSources, appKey));
  const connectedManualCrmAppKeys: string[] = MANUAL_CONNECT_CRM_IDS.filter(
    isManualCrmConnectedViaRollout
  ).flatMap((crm) => [...ROLLOUT_CRMS[crm].appKeys]);
  const [manualConnectCrm, setManualConnectCrm] = useState<ManualConnectCrmId | null>(null);
  // Super admins connect these CRMs in Rollout itself; everyone else uses the Connect form.
  const showManualCrmTiles = !restricted && !isSuperAdmin;
  const rolloutManualCrmAppKeys: string[] = isSuperAdmin
    ? [
        ...MANUAL_CONNECT_CRM_IDS.map((crm) => ROLLOUT_CRMS[crm].appKeys[0]),
        ...connectedManualCrmAppKeys,
      ]
    : connectedManualCrmAppKeys;
  const rolloutConnectorAppKeys: string[] = currentRolloutAppKeys(
    effectiveCrmFilter
      ? ROLLOUT_CRMS[effectiveCrmFilter].appKeys
      : [...ALL_ROLLOUT_CRM_APP_KEYS, ...rolloutManualCrmAppKeys]
  );
  const acceptsCredentialApp = (app: string) => rolloutConnectorAppKeys.includes(app);

  // Modal states for each integration
  const [isTwilioModalOpen, setIsTwilioModalOpen] = useState(false);
  const [isCRMModalOpen, setIsCRMModalOpen] = useState(false);
  const [isIntegrationModalOpen, setIsIntegrationModalOpen] = useState(false);
  const [isCINCModalOpen, setIsCINCModalOpen] = useState(false);
  const [isOAuthModalOpen, setIsOAuthModalOpen] = useState(false);
  const [isStagesModalOpen, setIsStagesModalOpen] = useState(false);
  const [isLegacyFubModalOpen, setIsLegacyFubModalOpen] = useState(false);
  // Auto-acknowledge if org already consented (in onboardingData) OR already
  // has any connected CRM via Rollout. Accept legacy camelCase
  // `crmConsentAcknowledged` from old Firestore onboarding.
  const onboardingData = selectedOrg?.onboardingData as
    | { crm_consent_acknowledged?: boolean; crmConsentAcknowledged?: boolean }
    | undefined;
  const baseOnboardingData = selectedOrg?.onboardingData ?? {};
  const hasExistingConsent = Boolean(
    onboardingData?.crm_consent_acknowledged ||
      onboardingData?.crmConsentAcknowledged ||
      hasAnyConnectedCrm({
        crmConnections: selectedOrg?.crmConnections,
        rollout: selectedOrg?.rollout,
      })
  );
  const [hasAcknowledgedAI, setHasAcknowledgedAI] = useState(hasExistingConsent);

  // FUB subdomain state - canonical home is `fubSetup.subdomain`; legacy
  // `rollout.fub.subdomain` is read as a fallback for orgs still on the old shape.
  const fubSubdomainPersisted =
    selectedOrg?.fubSetup?.subdomain || selectedOrg?.rollout?.fub?.subdomain || '';
  const [fubSubdomain, setFubSubdomain] = useState(fubSubdomainPersisted);
  const [isEditingSubdomain, setIsEditingSubdomain] = useState(false);

  // Track local disconnect so stages hide immediately without waiting for org refresh
  const [didDisconnectFub, setDidDisconnectFub] = useState(false);

  // Reset disconnect flag when org changes
  useEffect(() => {
    setDidDisconnectFub(false);
  }, [selectedOrg?.id]);

  // Sync local state whenever the persisted consent flag changes — covers
  // selectedOrg loading after mount and the consent being saved via the
  // checkbox handler below.
  useEffect(() => {
    setHasAcknowledgedAI(hasExistingConsent);
  }, [hasExistingConsent]);

  // Super admins should never be presented with the client-consent gate —
  // they're approving on behalf of someone else and shouldn't be able to
  // accidentally accept the contact-consent declaration for the org.
  const showConsentGate = requireAIAcknowledgement && !isSuperAdmin && !hasAcknowledgedAI;
  const showManagedSyncDirection = import.meta.env.VITE_APP_ENV === 'production' &&
    effectiveCrmFilter !== 'fub' && effectiveCrmFilter !== 'brivity';

  // Check if FUB is connected via Rollout — handles new credentials[] shape
  // and legacy rollout.fub.connected shape.
  const isFubConnected =
    isCrmConnectedViaRollout(
      { crmConnections: selectedOrg?.crmConnections, rollout: selectedOrg?.rollout },
      'follow-up-boss'
    ) && !didDisconnectFub;

  const syncStatus = selectedOrg?.last_initial_sync;

  // Prefer the FUB connectionId from the typed crmConnections list (new API).
  // Fall back to orgId — the backend still resolves that to the active
  // connection for orgs whose GQL hasn't repopulated crmConnections yet.
  const fubConnectionId =
    selectedOrg?.crmConnections?.find(
      (c) => c.app === 'follow-up-boss' && (c.status === 'active' || c.status === 'connected')
    )?.connectionId ?? selectedOrg?.id;

  // Stages are sourced from the API (single source of truth) so that the
  // optimistic update in useRolloutStages can reflect a save instantly without
  // waiting for the GQL org refetch.
  const { stages: fubStages, isLoading: isLoadingFubStages } = useRolloutStages({
    orgId: fubConnectionId,
    enabled: isOpen && isFubConnected,
  });
  const enabledStagesCount = fubStages.filter((s) => s.enabled).length;
  const isSyncing = syncStatus?.status === 'processing';

  // Check if legacy FUB is connected (via API key)
  const isLegacyFubConnected = Boolean(selectedOrg?.crmApiKeyHash);

  // Dynamically load/unload Rollout CSS only when modal is open to prevent global style leaks
  useEffect(() => {
    if (isOpen) {
      // Check if CSS is already loaded
      const existingLink = document.getElementById('rollout-css');
      if (!existingLink) {
        const linkElement = document.createElement('link');
        linkElement.id = 'rollout-css';
        linkElement.rel = 'stylesheet';
        linkElement.href = rolloutCssUrl;
        document.head.appendChild(linkElement);
      }
    } else {
      // Cleanup: remove the CSS when modal closes
      const existingLink = document.getElementById('rollout-css');
      if (existingLink) {
        existingLink.remove();
      }
    }
  }, [isOpen]);

  // Rollout token for Follow Up Boss - using React Query (no useEffect needed!)
  const {
    data: rolloutToken,
    isLoading: tokenLoading,
    error: tokenError,
  } = useRolloutToken({
    orgId: selectedOrg?.id,
    authContext: { uid: authUser?.uid, userId: user?.id,
      sessionEpoch: authEpoch.current.count, role: user?.role ?? null },
    enabled: widgetReady,
  });

  // CRM connection handlers - saves state to Firestore (rollout.<crm>.connected)
  const { handleCredentialAdded: baseHandleCredentialAdded,
    handleCredentialDeleted, isConnecting, isDisconnecting } =
    useRolloutCrmConnection({
      selectedOrg,
      onSuccess,
      isCurrent: isCurrentCallback,
    });

  const { updateSettings, isPending: isSavingSubdomainViaApi } = useUpdateOrganizationSettings(onSuccess);
  const { updateSettings: persistCrmConsent } = useUpdateOrganizationSettings();

  // Existing CRM connections count as consent in this UI. Persist that inferred
  // acknowledgment before calling the server-side binding/activation endpoints,
  // which intentionally require persisted consent.
  const ensureCrmConsentPersisted = async (app: string) => {
    if (!isCurrentCallback()) return false;
    const serverConsentProtectedApp = CONSENT_PROTECTED_CRM_APPS.includes(app);
    if (!serverConsentProtectedApp) return true;
    if (isSuperAdmin) return true;
    if (!selectedOrg?.id) return false;
    const consentAcknowledged = requireAIAcknowledgement
      ? hasAcknowledgedAI
      : onboardingConsentAcknowledged;
    if (!consentAcknowledged) {
      showToast({
        message: 'Acknowledge the CRM consent notice before connecting this integration.',
        variant: 'error',
      });
      return false;
    }
    if (onboardingData?.crm_consent_acknowledged === true ||
        onboardingData?.crmConsentAcknowledged === true) return true;
    try {
      // Do not invoke the modal's onSuccess/refetch callback here. It can change
      // the callback generation while a Rollout credential callback is pending.
      await persistCrmConsent({
        id: selectedOrg.id,
        data: {
          onboardingData: {
            ...baseOnboardingData,
            crm_consent_acknowledged: true,
            crm_consent_acknowledged_at: new Date().toISOString(),
          },
        } as any, // eslint-disable-line @typescript-eslint/no-explicit-any
      });
      return isCurrentCallback();
    } catch {
      showToast({
        message: 'Could not save CRM consent. Please try again before connecting.',
        variant: 'error',
      });
      return false;
    }
  };

  const [crmStatus, setCrmStatus] = useState<{
    generation: number;
    phase: 'loading' | 'ready' | 'error';
    data?: ProductionCrmConnectionStatus;
  } | null>(null);
  const crmStatusRequest = useRef(0);
  const shownCrmStatus = crmStatus?.generation === callbackGeneration ? crmStatus : null;
  const currentManagedCrm = shownCrmStatus?.phase === 'ready' &&
    shownCrmStatus.data?.app && isManagedCrmApp(shownCrmStatus.data.app) &&
    (!effectiveCrmFilter || (ROLLOUT_CRMS[effectiveCrmFilter].appKeys as readonly string[])
      .includes(shownCrmStatus.data.app))
    ? shownCrmStatus.data : null;

  useEffect(() => {
    if (!widgetReady || !showManagedSyncDirection || !selectedOrg?.id) return;
    const generation = callbackGeneration;
    const request = ++crmStatusRequest.current;
    let cancelled = false;
    setCrmStatus({ generation, phase: 'loading' });
    void getProductionCrmConnectionStatus(selectedOrg.id).then((data) => {
      if (!cancelled && callbackContext.current.generation === generation &&
          crmStatusRequest.current === request) {
        setCrmStatus({ generation, phase: 'ready', data });
      }
    }).catch(() => {
      if (!cancelled && callbackContext.current.generation === generation &&
          crmStatusRequest.current === request) {
        setCrmStatus({ generation, phase: 'error' });
      }
    });
    return () => { cancelled = true; };
  }, [widgetReady, showManagedSyncDirection, selectedOrg?.id, callbackGeneration]);

  // Wrapper to persist consent before the server-side connection check.
  const handleCredentialAdded = async (credential: { id: string; appKey: string }) => {
    if (!isCurrentCallback()) {
      if (callbackMounted.current && authUser?.uid && !authLoading) {
        showToast({
          message: 'CRM credential saved in Rollout. Reopen this organization’s integrations to check its connection status.',
          variant: 'info',
        });
      }
      return;
    }
    if (!acceptsCredentialApp(credential.appKey)) {
      showToast({ message: 'This CRM connector is not available for the selected integration.', variant: 'error' });
      return;
    }
    if (!await ensureCrmConsentPersisted(credential.appKey)) return;
    setDidDisconnectFub(false);
    await baseHandleCredentialAdded({
      ...credential,
      subdomain: credential.appKey === 'follow-up-boss' ? fubSubdomain : undefined,
    });
    if (showManagedSyncDirection && isManagedCrmApp(credential.appKey) &&
        selectedOrg?.id && isCurrentCallback()) {
      const request = ++crmStatusRequest.current;
      setCrmStatus({ generation: callbackGeneration, phase: 'loading' });
      try {
        const data = await getProductionCrmConnectionStatus(selectedOrg.id);
        if (isCurrentCallback() && crmStatusRequest.current === request) {
          setCrmStatus({ generation: callbackGeneration, phase: 'ready', data });
        }
      } catch {
        if (isCurrentCallback() && crmStatusRequest.current === request) {
          setCrmStatus({ generation: callbackGeneration, phase: 'error' });
        }
      }
    }
  };

  // Wrapper to hide stages immediately on disconnect
  const handleCredentialDeletedWithHide = async (credential: { id: string; appKey: string }) => {
    if (!isCurrentCallback()) {
      if (callbackMounted.current && authUser?.uid && !authLoading) {
        showToast({
          message: 'CRM credential changed in Rollout. Reopen this organization’s integrations to refresh Speculo.',
          variant: 'info',
        });
      }
      return;
    }
    if (!acceptsCredentialApp(credential.appKey)) {
      showToast({ message: 'This CRM connector is not available for the selected integration.', variant: 'error' });
      return;
    }
    await handleCredentialDeleted(credential);
    if (showManagedSyncDirection && isManagedCrmApp(credential.appKey) &&
        selectedOrg?.id && isCurrentCallback()) {
      const request = ++crmStatusRequest.current;
      setCrmStatus({ generation: callbackGeneration, phase: 'loading' });
      try {
        const data = await getProductionCrmConnectionStatus(selectedOrg.id);
        if (isCurrentCallback() && crmStatusRequest.current === request) {
          setCrmStatus({ generation: callbackGeneration, phase: 'ready', data });
        }
      } catch {
        if (isCurrentCallback() && crmStatusRequest.current === request) {
          setCrmStatus({ generation: callbackGeneration, phase: 'error' });
        }
      }
    }
    if (isCurrentCallback() && credential.appKey === 'follow-up-boss') {
      setDidDisconnectFub(true);
    }
  };

  // Combined loading state for FUB operations
  const isFubLoading = isConnecting || isDisconnecting;

  // Twilio is configured if a phone number is set (we may not have SID/token stored)
  const isTwilioConfigured = () => {
    return Boolean(selectedOrg?.phoneNumbers?.outbound || selectedOrg?.phoneNumbers?.inbound);
  };

  // Migrating to Rollout — commented out with CINC/Brivity sections
  // const isCRMConfigured = () => {
  //   return Boolean(selectedOrg?.externalClient);
  // };

  // const isCINCConfigured = () => {
  //   return Boolean(selectedOrg?.cincSetup?.connected);
  // };

  const [oauthVerified, setOauthVerified] = useState(false);

  // Check OAuth status from the API on modal open (org.oauthSetup may be stale/null in GQL)
  useEffect(() => {
    if (!isOpen || !selectedOrg?.id) return;
    setOauthVerified(false);
    getOAuthClientInfo(selectedOrg.id)
      .then((info) => { if (info?.clientId) setOauthVerified(true); })
      .catch(() => {});
  }, [isOpen, selectedOrg?.id]);

  const isOAuthConfigured = () => {
    return oauthVerified || Boolean(
      selectedOrg?.oauthSetup?.created_at ||
      selectedOrg?.oauthSetup?.createdAt ||
      selectedOrg?.oauthSetup?.clientId ||
      selectedOrg?.oauthSetup?.client_id
    );
  };

  // Handle success from child modals
  const handleSuccess = () => {
    onSuccess?.();
  };

  const isSavingSubdomain = isSavingSubdomainViaApi;

  // Save subdomain via Remi API
  const handleSaveSubdomain = async () => {
    if (!selectedOrg?.id) return;

    const validation = validateSubdomain(fubSubdomain);
    if (!validation.isValid) {
      showToast({
        message: validation.error || 'Invalid subdomain format',
        variant: 'error',
      });
      return;
    }

    try {
      // Canonical store for FUB subdomain is `fubSetup.subdomain` — that's the
      // column the backend reads (see remi-api onboarding.py secrets-build).
      // Writing here also keeps us out of `rollout_config`, where the Rollout
      // webhook owns the credentials list and our writes risk racing with it.
      await updateSettings({
        id: selectedOrg.id,
        data: {
          fubSetup: {
            ...(selectedOrg.fubSetup ?? {}),
            subdomain: fubSubdomain?.toLowerCase() || null,
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any,
      });
      setIsEditingSubdomain(false);
    } catch {
      console.error('Failed to save subdomain');
      showToast({
        message: 'Failed to save subdomain. Please try again.',
        variant: 'error',
      });
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <Modal.Header>
          <Typography variant="h3">{effectiveCrmFilter
            ? `${ROLLOUT_CRMS[effectiveCrmFilter].label} Setup` : 'Integrations'}</Typography>
        </Modal.Header>
        <Modal.Body className="pb-8">
          <AdminLockOverlay locked={!isAdmin} subject="Integrations">
          <div className="space-y-3">
            {!restricted && (
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <p className="text-gray-600 dark:text-gray-400">Configure integrations with third-party services.</p>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {isOAuthConfigured() && (
                    <div className="px-2 py-1 bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 text-xs rounded-full whitespace-nowrap">
                      Active
                    </div>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsOAuthModalOpen(true)}
                    disabled={!isSuperAdmin}
                    className="flex items-center gap-2"
                  >
                    <Code className="w-4 h-4" />
                    <span className="hidden sm:inline">OAuth</span>
                  </Button>
                </div>
              </div>
            )}
            <div className="space-y-4">
              {/* Twilio Integration - Super Admin Only */}
              {isSuperAdmin && !hideTwilio && (
                <div className="border border-gray-300 dark:border-white/15 rounded-lg p-4">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-full flex-shrink-0">
                        <MessageSquare className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <Typography variant="h5" className="font-semibold">
                          Twilio
                        </Typography>
                        {isTwilioConfigured() && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {formatPhoneNumber(selectedOrg?.phoneNumbers?.outbound || selectedOrg?.phoneNumbers?.inbound)}
                            {' \u00b7 '}Enter credentials to update phone number
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {isTwilioConfigured() && (
                        <div className="px-2 py-1 bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 text-xs rounded-full whitespace-nowrap">
                          Configured
                        </div>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsTwilioModalOpen(true)}
                        className="flex items-center gap-1"
                      >
                        <Edit className="w-4 h-4" />
                        <span className="hidden sm:inline">
                          {isTwilioConfigured() ? 'Update' : 'Setup'}
                        </span>
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* CRM Integrations - Rollout OAuth */}
              <div
                className={`border border-gray-300 dark:border-white/15 rounded-lg p-4 ${
                  isLegacyFubConnected ? 'bg-gray-50 dark:bg-white/5' : ''
                }`}
              >
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-full flex-shrink-0">
                      <RotateCw className="h-5 w-5 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <Typography variant="h5" className="font-semibold">
                        CRM Integrations
                      </Typography>
                      {isLegacyFubConnected && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          Please disconnect Legacy FUB first
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Show blocked message when legacy FUB is connected */}
                {isLegacyFubConnected ? (
                  <div className="py-4 px-3 bg-gray-100 dark:bg-white/5 rounded border border-gray-200 dark:border-white/10">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      CRM integrations are not available while the Legacy FUB integration
                      is active. Please disconnect the Legacy integration below to use this version.
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Pro tip about speculo_sync tag - only show when FUB not connected */}
                    {!isFubConnected && (
                      <div className="mb-3">
                        <TipBox>
                          <strong>Pro tip:</strong> Before connecting your CRM, we highly recommend
                          applying the{' '}
                          <code className="bg-yellow-200 dark:bg-yellow-800/40 px-1 rounded">speculo_sync</code> tag to
                          the contacts you want Remi (AI) to engage with to help speed up the
                          initial sync process especially for large databases. This tag does not
                          trigger any communication. You can still move forward without applying
                          the tag. Additional contacts can be synced over at any time after the
                          integration is established.
                        </TipBox>
                      </div>
                    )}

                    {/* Sync Status Banners - only show if FUB is connected */}
                    {isFubConnected && syncStatus && isSyncing && (
                      <RolloutSyncStatusBanner syncStatus={syncStatus} />
                    )}
                    {isFubConnected && syncStatus && !isSyncing && (
                      <RolloutSyncResults syncStatus={syncStatus} />
                    )}

                    {/* Loading state */}
                    {tokenLoading && (
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                        <RotateCw className="w-4 h-4 animate-spin" />
                        <span className="text-sm">Loading...</span>
                      </div>
                    )}

                    {/* Error state */}
                    {tokenError && (
                      <div className="text-red-500 text-sm">
                        Failed to load CRM integrations
                      </div>
                    )}

                    {/* Rollout CredentialsManager — FUB, CINC, Brivity (always visible so user picks CRM first) */}
                    {showManagedSyncDirection &&
                      <div className="mb-3">
                        <p className="block text-sm font-medium">
                          CRM record sync direction
                        </p>
                        <p>New managed CRM connections: Bidirectional CRM records</p>
                        <p className="text-xs">Bidirectional sync can update CRM records. This does not enable calls, texts, or emails.</p>
                        {currentManagedCrm?.status === 'connected' &&
                          <p role="status">Current CRM sync: {currentManagedCrm.sync_direction === 'bidirectional'
                            ? 'Bidirectional' : currentManagedCrm.sync_direction === 'inbound'
                              ? 'Inbound only' : 'Direction unavailable'}</p>}
                        {currentManagedCrm?.status === 'sync_paused' &&
                          <p role="status">Current CRM sync is paused. Stored direction: {currentManagedCrm.sync_direction ?? 'unavailable'}.</p>}
                        {currentManagedCrm?.status === 'pending_setup' &&
                          <p role="status">Current CRM connection is pending setup; bidirectional sync is not active.</p>}
                      </div>}
                    {!widgetReady && isOpen && (
                      <p role="status" className="text-sm text-gray-600 dark:text-gray-400">
                        Waiting for the current organization and signed-in session before loading CRM integrations.
                      </p>
                    )}
                    {widgetReady && rolloutToken && !tokenError && (
                      <div className={`relative rollout-container ${showConsentGate ? 'pointer-events-none opacity-60' : ''}`}>
                        {/* Loading overlay during connect/disconnect */}
                        {isFubLoading && (
                          <div className="absolute inset-0 bg-white/70 dark:bg-black/50 flex items-center justify-center z-10 rounded">
                            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                              <RotateCw className="w-4 h-4 animate-spin" />
                              <span className="text-sm">
                                {isConnecting ? 'Connecting...' : 'Disconnecting...'}
                              </span>
                            </div>
                          </div>
                        )}
                        <RolloutLinkProvider token={rolloutToken}>
                          <CredentialsManager
                            apiCategories={{ crm: true }}
                            onCredentialAdded={handleCredentialAdded}
                            onCredentialDeleted={handleCredentialDeletedWithHide}
                            shouldRenderConnector={(connector: { appKey?: string }) => {
                              return Boolean(connector.appKey && rolloutConnectorAppKeys.includes(connector.appKey));
                            }}
                          />
                        </RolloutLinkProvider>
                      </div>
                    )}
                    {/* CRMs connected through Speculo's Connect form, as tiles like Rollout's list */}
                    {showManualCrmTiles && selectedOrg && (
                      <div
                        className={`mt-4 grid grid-cols-[repeat(auto-fill,minmax(130px,1fr))] gap-4 ${showConsentGate ? 'pointer-events-none opacity-60' : ''}`}
                      >
                        {MANUAL_CONNECT_CRM_IDS.filter(
                          (crm) => !isManualCrmConnectedViaRollout(crm)
                        ).map((crm) => {
                          const request = getCrmConnectRequest(selectedOrg.onboardingData, crm);
                          return (
                            <button
                              key={crm}
                              type="button"
                              onClick={() => setManualConnectCrm(crm)}
                              className="relative flex flex-col justify-center aspect-square p-3 rounded-lg border border-black/10 dark:border-white/10 shadow-[0_0_6px_#0000000f] hover:shadow-[0_0_10px_#00000026] transition-shadow text-center"
                            >
                              <img
                                src={MANUAL_CONNECT_CRMS[crm].icon}
                                alt=""
                                className="block mx-auto w-12 aspect-square"
                              />
                              <span className="mt-3.5 text-sm font-semibold text-gray-900 dark:text-gray-100">
                                {ROLLOUT_CRMS[crm].label}
                              </span>
                              {request && (
                                <span className="mt-1 text-xs text-green-700 dark:text-green-400">
                                  Submitted {new Date(request.submitted_at).toLocaleDateString()}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {showManualCrmTiles && selectedOrg && (
                      <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                        {CRM_TRADEMARK_NOTICE}
                      </p>
                    )}
                    {/* Consent checkbox — hidden for super admins so they can't
                        accidentally accept the contact-consent declaration on
                        behalf of the org. */}
                    {showConsentGate && (
                      <div className="mt-3 p-4 border border-amber-200 dark:border-amber-500/30 rounded-lg bg-amber-50 dark:bg-amber-900/10">
                        <div className="flex items-start gap-3">
                          <Checkbox
                            id="crm-ai-acknowledgement"
                            checked={hasAcknowledgedAI}
                            onChange={async (e) => {
                              const checked = e.target.checked;
                              setHasAcknowledgedAI(checked);
                              if (!checked || !selectedOrg?.id) return;
                              try {
                                await updateSettings({
                                  id: selectedOrg.id,
                                  data: {
                                    onboardingData: {
                                      ...baseOnboardingData,
                                      crm_consent_acknowledged: true,
                                      crm_consent_acknowledged_at: new Date().toISOString(),
                                    },
                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                  } as any,
                                });
                              } catch {
                                // Non-blocking — revert local state so the user can retry
                                setHasAcknowledgedAI(false);
                              }
                            }}
                          />
                          <label htmlFor="crm-ai-acknowledgement" className="text-xs text-foreground/80 leading-relaxed cursor-pointer whitespace-pre-line">
                            {aiAcknowledgementCopy}
                          </label>
                        </div>
                      </div>
                    )}

                    {/* FUB Stages Configuration - shown when FUB is connected (hide for non-FUB filters) */}
                    {isFubConnected && (!effectiveCrmFilter || effectiveCrmFilter === 'fub') && (
                      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-white/10">
                        <Typography variant="small" className="font-semibold text-gray-500 dark:text-gray-400 mb-2">
                          Follow Up Boss
                        </Typography>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Settings className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                              Stages:{' '}
                              {isLoadingFubStages
                                ? 'Loading...'
                                : enabledStagesCount > 0
                                  ? `${enabledStagesCount} configured`
                                  : 'Not configured'}
                            </span>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setIsStagesModalOpen(true)}
                            className="flex items-center gap-1 h-8 px-4"
                          >
                            <Settings className="w-4 h-4" />
                            <span className="hidden sm:inline">Configure Stages</span>
                          </Button>
                        </div>

                        {/* FUB Subdomain Configuration */}
                        <div className="flex items-center justify-between mt-3">
                          <div className="flex items-center gap-2">
                            <Link className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                            {isEditingSubdomain ? (
                              <div className="flex items-center gap-2">
                                <Input
                                  value={fubSubdomain}
                                  onChange={(e) => setFubSubdomain(e.target.value.trim().toLowerCase())}
                                  placeholder="yourcompany"
                                  className="w-32"
                                  autoFocus
                                />
                                <span className="text-xs text-gray-500 dark:text-gray-400">.followupboss.com</span>
                              </div>
                            ) : (
                              <span className="text-sm text-gray-600 dark:text-gray-400">
                                Subdomain:{' '}
                                {fubSubdomainPersisted
                                  ? `${fubSubdomainPersisted}.followupboss.com`
                                  : 'Not configured'}
                              </span>
                            )}
                          </div>
                          {isEditingSubdomain ? (
                            <div className="flex items-center gap-1">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={handleSaveSubdomain}
                                disabled={isSavingSubdomain}
                                className="flex items-center gap-1 h-8 px-2"
                              >
                                {isSavingSubdomain ? (
                                  <RotateCw className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Check className="w-4 h-4 text-green-600" />
                                )}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setFubSubdomain(fubSubdomainPersisted);
                                  setIsEditingSubdomain(false);
                                }}
                                disabled={isSavingSubdomain}
                                className="flex items-center gap-1 h-8 px-2"
                              >
                                <X className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                              </Button>
                            </div>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setIsEditingSubdomain(true)}
                              className="flex items-center gap-1 h-8 px-4"
                            >
                              <Edit className="w-4 h-4" />
                              <span className="hidden sm:inline">Edit</span>
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Coming Soon Alert (hidden in fubOnly mode) */}
            {!restricted && (
              <SpeculoAlert variant="info">
                <Info className="h-4 w-4" />
                <AlertDescription>
                  Choose your CRM to connect it to Speculo.
                </AlertDescription>
              </SpeculoAlert>
            )}
          </div>
          </AdminLockOverlay>
        </Modal.Body>
      </Modal>

      {/* Child Modals */}
      <TwilioModal
        isOpen={isTwilioModalOpen}
        onClose={() => setIsTwilioModalOpen(false)}
        selectedOrg={selectedOrg}
        onSuccess={handleSuccess}
      />

      <CRMModal
        isOpen={isCRMModalOpen}
        onClose={() => setIsCRMModalOpen(false)}
        selectedOrg={selectedOrg}
        onSuccess={handleSuccess}
      />

      {/* Website Integration Instructions Modal */}
      <WebsiteIntegrationModal
        isOpen={isIntegrationModalOpen}
        onClose={() => setIsIntegrationModalOpen(false)}
        orgId={selectedOrg?.id}
      />

      <CINCModal
        isOpen={isCINCModalOpen}
        onClose={() => setIsCINCModalOpen(false)}
        selectedOrg={selectedOrg}
        onSuccess={handleSuccess}
      />

      <SpeculoOAuthModal
        isOpen={isOAuthModalOpen}
        onClose={() => setIsOAuthModalOpen(false)}
        selectedOrg={selectedOrg}
        onSuccess={() => {
          setOauthVerified(true);
          handleSuccess();
        }}
      />

      {/* Speculo Connect form for CRMs the team connects by hand */}
      {manualConnectCrm && selectedOrg && (
        <ManualCrmConnectModal
          crm={manualConnectCrm}
          organization={selectedOrg}
          source="client_admin"
          onClose={() => setManualConnectCrm(null)}
          onSubmitted={() => onSuccess?.()}
        />
      )}

      {/* FUB Stages Configuration Modal */}
      <FubStagesModal
        isOpen={isStagesModalOpen}
        onClose={() => setIsStagesModalOpen(false)}
        orgId={fubConnectionId}
        onSuccess={handleSuccess}
      />

      {/* Legacy Follow Up Boss Modal */}
      <FollowUpBossModal
        isOpen={isLegacyFubModalOpen}
        onClose={() => setIsLegacyFubModalOpen(false)}
        selectedOrg={selectedOrg}
        onSuccess={handleSuccess}
      />

      {/* Legacy Follow Up Boss Modal */}
      <FollowUpBossModal
        isOpen={isLegacyFubModalOpen}
        onClose={() => setIsLegacyFubModalOpen(false)}
        selectedOrg={selectedOrg}
        onSuccess={handleSuccess}
      />

      {/* Legacy Follow Up Boss Modal */}
      <FollowUpBossModal
        isOpen={isLegacyFubModalOpen}
        onClose={() => setIsLegacyFubModalOpen(false)}
        selectedOrg={selectedOrg}
        onSuccess={handleSuccess}
      />
    </>
  );
};

// The CINC, Lofty, and Sierra production test orgs use the same filtered
// Rollout widget as ordinary organizations for credential disconnect/reconnect.
export const IntegrationsModal: React.FC<IntegrationsModalProps> = (props) => {
  const orgId = props.selectedOrgId ?? props.selectedOrg?.id;
  if (import.meta.env.VITE_APP_ENV === 'production' && isProductionTestCrmOrg(orgId)) {
    if (orgId === '70299cd6-a817-4eeb-a333-df660b1f204e' ||
        orgId === '816d1e20-f38b-466e-96ec-46bb7d0f404c' ||
        orgId === '6e5adb4f-c2b4-459b-8c40-f41bb801bbf2') {
      const app = PRODUCTION_TEST_CRMS[orgId].app;
      const crmFilter: RolloutCrmId = app === 'sierra-interactive' ? 'sierra_interactive' : app;
      return <OrdinaryIntegrationsModal {...props} crmFilter={crmFilter} />;
    }
    return <ExistingProductionCrmBinding isOpen={props.isOpen} onClose={props.onClose}
      orgId={orgId} selectedOrgId={props.selectedOrgId} currentRowId={props.currentRowId}
      freshDetailId={props.freshDetailId} freshDetailLoading={props.freshDetailLoading}
      onSuccess={props.onSuccess} />;
  }
  return <OrdinaryIntegrationsModal {...props} />;
};
